import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Sparkles, Zap } from "lucide-react";
import {
  fetchSimulationCredits,
  useSimulationCredit,
} from "@/lib/simulation-credits.functions";
import { startCashfreeCheckout } from "@/lib/cashfree.functions";
import { openCashfreeCheckout } from "@/lib/cashfree-client";
import { useUpgrade, formatInr } from "@/components/billing/premium-lock";
import { useOptionalAuthGate } from "@/components/auth/auth-gate";

const CREDITS_KEY = ["simulation-credits"];

/** Live credit state for any surface that wants to show the balance. */
export function useSimulationGate() {
  const query = useQuery({
    queryKey: CREDITS_KEY,
    queryFn: () => fetchSimulationCredits(),
    staleTime: 30_000,
  });
  const balance = query.data?.balance ?? 0;
  return {
    checking: query.isLoading,
    allowed: balance > 0,
    balance,
    state: query.data ?? null,
    reason: balance > 0 ? ("OK" as const) : ("NO_CREDITS" as const),
  };
}

/** Buys +300 credits through the existing production Cashfree checkout. */
export function BuyCreditsButton({
  addonPaise,
  canBuyAddon,
  className = "",
}: {
  addonPaise: number;
  canBuyAddon: boolean;
  className?: string;
}) {
  const startCheckout = useServerFn(startCashfreeCheckout);
  const { openUpgrade } = useUpgrade();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buy = async () => {
    if (!canBuyAddon) {
      openUpgrade("Subscribe to unlock simulations and get 300 simulation credits.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const session = await startCheckout({
        data: {
          plan_id: "addon",
          simulation_addon_selected: true,
          payment_type: "simulation_addon",
        },
      });
      await openCashfreeCheckout({
        paymentSessionId: session.paymentSessionId,
        mode: session.mode,
      });
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err ?? "");
      setError(
        raw.includes("NO_ACTIVE_SUBSCRIPTION")
          ? "You need an active subscription to buy the add-on."
          : raw.includes("CASHFREE_NOT_CONFIGURED")
            ? "Payments are not switched on yet. Please try again shortly."
            : "We couldn't open the payment page. Please try again.",
      );
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      <button
        type="button"
        disabled={busy}
        onClick={() => void buy()}
        className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-xs font-bold text-primary-foreground shadow-sm transition active:scale-[0.98] disabled:opacity-60"
      >
        {busy ? (
          <Loader2 className="size-3.5 animate-spin" aria-hidden />
        ) : (
          <Sparkles className="size-3.5" aria-hidden />
        )}
        +300 Simulations — {formatInr(addonPaise)}
      </button>
      {error ? (
        <p role="alert" className="mt-2 text-[11px] font-medium text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Small always-visible balance chip. */
export function SimulationCreditsBadge({ balance }: { balance: number }) {
  return (
    <p className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-card/60 px-3 py-1 text-[11px] font-semibold">
      <Zap className="size-3.5 text-primary" aria-hidden />
      Simulation Credits: {balance}
    </p>
  );
}

/**
 * Server-authoritative gate. One credit is spent per simulation opened (the
 * spend is idempotent per simulation per day), and a zero balance shows the
 * ₹29 top-up instead of the content.
 */
export function SimulationGate({ children, simulationId }: { children: ReactNode; simulationId?: string }) {
  const gate = useOptionalAuthGate();
  const queryClient = useQueryClient();
  const spend = useServerFn(useSimulationCredit);
  const { state, checking, balance } = useSimulationGate();
  const [spent, setSpent] = useState<boolean | null>(null);

  useEffect(() => {
    let alive = true;
    if (!state || !state.signedIn || state.isGuest || !simulationId) return;
    if (state.balance <= 0) {
      setSpent(false);
      return;
    }
    void spend({ data: { simulation_id: simulationId } })
      .then((res) => {
        if (!alive) return;
        setSpent(res.allowed);
        queryClient.setQueryData(CREDITS_KEY, (old: typeof state | undefined) =>
          old ? { ...old, balance: res.balance } : old,
        );
      })
      .catch(() => {
        if (alive) setSpent(false);
      });
    return () => {
      alive = false;
    };
  }, [state, simulationId, spend, queryClient]);

  // Surfaces that do not consume credits (virtual labs) stay open as before.
  if (!simulationId) return <>{children}</>;

  if (checking) {
    return (
      <div className="glass grid h-[220px] place-items-center rounded-[22px] text-xs text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden />
      </div>
    );
  }

  if (!state?.signedIn || state.isGuest) {
    return (
      <div className="glass rounded-[22px] p-6 text-center">
        <h2 className="text-sm font-extrabold tracking-tight">Sign in to run simulations</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Simulation credits are saved to your account.
        </p>
        <button
          type="button"
          onClick={() => gate?.openAuthModal()}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-xs font-bold text-primary-foreground"
        >
          Create a free account
        </button>
      </div>
    );
  }

  const outOfCredits = balance <= 0 && spent !== true;

  if (outOfCredits) {
    return (
      <div className="glass rounded-[22px] p-6 text-center">
        <SimulationCreditsBadge balance={0} />
        <h2 className="mt-3 text-sm font-extrabold tracking-tight">
          You&apos;ve used all your simulation credits.
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Top up instantly and keep experimenting.
        </p>
        <div className="mt-4 flex justify-center">
          <BuyCreditsButton addonPaise={state.addonPaise} canBuyAddon={state.canBuyAddon} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <SimulationCreditsBadge balance={balance} />
      </div>
      {children}
    </div>
  );
}
